// Éditeur de base de connaissances board. Saisie/édition d'un modèle (rails,
// points de mesure, symptômes), suppression, et import/export JSON pour gérer
// tes cartes (820-xxxxx) en masse ou les partager.

import type { BoardModel, MeasurePoint, PowerState, Rail } from "../domain/types";
import { deleteBoard, listBoards, putBoard } from "../storage/db";
import { el, esc, toast } from "./dom";

const STATES: PowerState[] = ["G3", "always_on", "S5", "S4", "S3", "S0"];
const MTYPES = ["voltage", "resistance", "diode", "continuity"] as const;

let onChange: (() => void) | undefined;

export async function renderBoardEditor(container: HTMLElement, changed?: () => void): Promise<void> {
  onChange = changed;
  const boards = await listBoards();
  container.innerHTML = "";

  const picker = el(`
    <section class="card">
      <h2>Éditeur de base board</h2>
      <label>Modèle</label>
      <select id="be-pick"></select>
      <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;">
        <button class="btn sm" id="be-new">+ Nouveau</button>
        <button class="btn sm ghost" id="be-export">Exporter JSON</button>
        <button class="btn sm ghost" id="be-import">Importer JSON</button>
        <button class="btn sm ghost" id="be-pdf">📄 Importer schéma PDF</button>
        <input type="file" id="be-pdf-file" accept="application/pdf" style="display:none">
      </div>
      <div class="muted" style="margin-top:6px;">Schéma PDF « searchable » → brouillon de carte (rails + tensions auto-détectés), stocké sur ce poste, à vérifier dans l'éditeur.</div>
    </section>`);
  container.appendChild(picker);

  const pick = picker.querySelector("#be-pick") as HTMLSelectElement;
  pick.appendChild(el(`<option value="">— choisir —</option>`));
  for (const b of boards) pick.appendChild(el(`<option value="${esc(b.id)}">${esc(b.brand)} ${esc(b.model)}</option>`));

  const formHost = el(`<div id="be-form"></div>`);
  container.appendChild(formHost);

  pick.addEventListener("change", async () => {
    const b = boards.find((x) => x.id === pick.value);
    renderForm(formHost, b ?? null);
  });
  picker.querySelector("#be-new")!.addEventListener("click", () => {
    pick.value = "";
    renderForm(formHost, null);
  });
  picker.querySelector("#be-export")!.addEventListener("click", () => exportJSON());
  picker.querySelector("#be-import")!.addEventListener("click", () => importJSON(container));

  const pdfInput = picker.querySelector("#be-pdf-file") as HTMLInputElement;
  picker.querySelector("#be-pdf")!.addEventListener("click", () => pdfInput.click());
  pdfInput.addEventListener("change", async () => {
    const file = pdfInput.files?.[0];
    pdfInput.value = "";
    if (!file) return;
    await importPdf(container, file);
  });
}

async function importPdf(container: HTMLElement, file: File): Promise<void> {
  // Import lourd (pdf.js) chargé à la demande : n'alourdit pas le démarrage.
  const { importSchematicPdf, guessFromFilename } = await import("./schematicImport");
  const guess = guessFromFilename(file.name);
  const id = (window.prompt("Référence carte (820-xxxxx) :", guess.id) ?? "").trim();
  if (!id) return;
  const model = (window.prompt("Nom du modèle :", guess.model) ?? "").trim() || guess.model;

  toast("Analyse du PDF en cours…");
  try {
    const draft = await importSchematicPdf(file, id, model);
    if (draft.railCount === 0) {
      toast("Aucun rail détecté — PDF image (scan) ? Il faut un PDF « searchable » avec texte.");
      return;
    }
    await putBoard(draft.board);
    onChange?.();
    toast(`Brouillon « ${model} » créé : ${draft.railCount} rails sur ${draft.pages} pages. Vérifie les tensions !`);
    await renderBoardEditor(container, onChange);
    const pick = container.querySelector("#be-pick") as HTMLSelectElement | null;
    const form = container.querySelector("#be-form") as HTMLElement | null;
    if (pick && form) {
      pick.value = id;
      renderForm(form, draft.board);
    }
  } catch (e) {
    toast(`Échec de l'import : ${(e as Error).message}`);
  }
}

function rowInput(name: string, value: string, placeholder = "", width = ""): string {
  return `<input data-f="${name}" value="${esc(value)}" placeholder="${esc(placeholder)}" style="${width}">`;
}

function railRow(r?: Partial<Rail>): HTMLElement {
  const row = el(`<div class="erow">
    ${rowInput("name", r?.name ?? "", "PPBUS_G3H")}
    <select data-f="state">${STATES.map((s) => `<option ${r?.state === s ? "selected" : ""}>${s}</option>`).join("")}</select>
    ${rowInput("nominal_v", r?.nominal_v != null ? String(r.nominal_v) : "", "V", "max-width:70px")}
    ${rowInput("source_component", r?.source_component ?? "", "source")}
    ${rowInput("parent", r?.parent ?? "", "rail amont")}
    <button class="btn fb" data-del>✕</button>
  </div>`);
  row.querySelector("[data-del]")!.addEventListener("click", () => row.remove());
  return row;
}

function mpRow(m?: Partial<MeasurePoint>): HTMLElement {
  const row = el(`<div class="erow">
    ${rowInput("label", m?.label ?? "", "point de mesure")}
    ${rowInput("rail", m?.rail ?? "", "rail")}
    <select data-f="type">${MTYPES.map((t) => `<option ${m?.type === t ? "selected" : ""}>${t}</option>`).join("")}</select>
    ${rowInput("expected", m?.expected ?? "", "attendu (ex. 12.6 V)")}
    <button class="btn fb" data-del>✕</button>
  </div>`);
  row.querySelector("[data-del]")!.addEventListener("click", () => row.remove());
  return row;
}

function sympRow(id = "", label = "", checklist: string[] = []): HTMLElement {
  const row = el(`<div class="erow col">
    <div class="erow">
      ${rowInput("id", id, "id (ex. dead_board)")}
      ${rowInput("label", label, "libellé")}
      <button class="btn fb" data-del>✕</button>
    </div>
    <textarea data-f="checklist" placeholder="Une étape par ligne">${esc(checklist.join("\n"))}</textarea>
  </div>`);
  row.querySelector("[data-del]")!.addEventListener("click", () => row.remove());
  return row;
}

function readRows(host: HTMLElement): Record<string, string>[] {
  return [...host.querySelectorAll(":scope > .erow")].map((row) => {
    const o: Record<string, string> = {};
    row.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-f]").forEach((i) => {
      o[i.dataset.f as string] = i.value.trim();
    });
    return o;
  });
}

function renderForm(host: HTMLElement, b: BoardModel | null): void {
  host.innerHTML = "";
  const card = el(`
    <section class="card">
      <h2>${b ? "Modifier" : "Nouveau modèle"}</h2>
      <label>Référence carte (id)</label>${rowInput("id", b?.id ?? "", "820-01700")}
      <label>Marque</label>${rowInput("brand", b?.brand ?? "", "Apple")}
      <label>Modèle</label>${rowInput("model", b?.model ?? "", 'MacBook Pro 13" 2019 (A2141)')}
      <label>Alias (séparés par des virgules)</label>${rowInput("aliases", (b?.aliases ?? []).join(", "), "A2141, …")}
      <label>Rails</label><div id="be-rails"></div><button class="btn sm ghost" id="add-rail">+ Rail</button>
      <label style="margin-top:14px;">Points de mesure</label><div id="be-mps"></div><button class="btn sm ghost" id="add-mp">+ Point</button>
      <label style="margin-top:14px;">Symptômes</label><div id="be-symps"></div><button class="btn sm ghost" id="add-symp">+ Symptôme</button>
      <div style="display:flex;gap:8px;margin-top:16px;">
        <button class="btn" id="be-save" style="margin:0;flex:1;">Enregistrer</button>
        ${b ? `<button class="btn ghost" id="be-del" style="margin:0;">Supprimer</button>` : ""}
      </div>
    </section>`);
  host.appendChild(card);

  const rails = card.querySelector("#be-rails") as HTMLElement;
  const mps = card.querySelector("#be-mps") as HTMLElement;
  const symps = card.querySelector("#be-symps") as HTMLElement;
  (b?.rails ?? []).forEach((r) => rails.appendChild(railRow(r)));
  (b?.measure_points ?? []).forEach((m) => mps.appendChild(mpRow(m)));
  (b?.symptoms ?? []).forEach((s) => symps.appendChild(sympRow(s.id, s.label, s.checklist)));

  card.querySelector("#add-rail")!.addEventListener("click", () => rails.appendChild(railRow()));
  card.querySelector("#add-mp")!.addEventListener("click", () => mps.appendChild(mpRow()));
  card.querySelector("#add-symp")!.addEventListener("click", () => symps.appendChild(sympRow()));

  const fieldVal = (name: string) =>
    (card.querySelector(`[data-f="${name}"]`) as HTMLInputElement)?.value.trim() ?? "";

  card.querySelector("#be-save")!.addEventListener("click", async () => {
    const id = fieldVal("id");
    const model = fieldVal("model");
    if (!id || !model) return toast("Référence (id) et modèle sont obligatoires.");

    const board: BoardModel = {
      id,
      brand: fieldVal("brand") || "—",
      model,
      aliases: fieldVal("aliases").split(",").map((s) => s.trim()).filter(Boolean),
      updated_at: new Date().toISOString(),
      rails: readRows(rails)
        .filter((r) => r.name)
        .map((r) => ({
          name: r.name,
          state: (r.state || "G3") as PowerState,
          nominal_v: Number(r.nominal_v) || 0,
          source_component: r.source_component || undefined,
          parent: r.parent || null,
        })),
      measure_points: readRows(mps)
        .filter((m) => m.label)
        .map((m) => ({
          label: m.label,
          rail: m.rail || undefined,
          type: (m.type || "voltage") as MeasurePoint["type"],
          expected: m.expected || "",
        })),
      symptoms: [...symps.querySelectorAll(":scope > .erow.col")].map((row) => {
        const id2 = (row.querySelector('[data-f="id"]') as HTMLInputElement).value.trim();
        const label = (row.querySelector('[data-f="label"]') as HTMLInputElement).value.trim();
        const checklist = (row.querySelector('[data-f="checklist"]') as HTMLTextAreaElement).value
          .split("\n").map((s) => s.trim()).filter(Boolean);
        return { id: id2 || label.toLowerCase().replace(/\s+/g, "_"), label, checklist };
      }).filter((s) => s.label),
    };

    await putBoard(board);
    toast(`Modèle ${board.model} enregistré.`);
    onChange?.();
  });

  card.querySelector("#be-del")?.addEventListener("click", async () => {
    if (!b) return;
    await deleteBoard(b.id);
    toast(`Modèle ${b.model} supprimé.`);
    onChange?.();
  });
}

async function exportJSON(): Promise<void> {
  const boards = await listBoards();
  const blob = new Blob([JSON.stringify(boards, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "thor-boards.json";
  a.click();
  URL.revokeObjectURL(url);
}

function importJSON(container: HTMLElement): void {
  const inp = document.createElement("input");
  inp.type = "file";
  inp.accept = "application/json,.json";
  inp.addEventListener("change", async () => {
    const file = inp.files?.[0];
    if (!file) return;
    try {
      const arr = JSON.parse(await file.text()) as BoardModel[];
      if (!Array.isArray(arr)) throw new Error("format");
      let n = 0;
      for (const b of arr) {
        if (b && b.id && b.model) {
          await putBoard({ ...b, updated_at: b.updated_at ?? new Date().toISOString() });
          n++;
        }
      }
      toast(`${n} modèle(s) importé(s).`);
      void renderBoardEditor(container, onChange);
    } catch {
      toast("JSON invalide.");
    }
  });
  inp.click();
}
